// MobileCatalogCard — compact `.mcb-grid .ra-cat-card` for the 2-column mobile
// browse grid. Same content as the desktop CatalogCard but tighter (148px min,
// 14px pad, 15/12/13 type) and an azurite tint when added, matching the design.

import { Box, Icon } from "@chakra-ui/react";
import { TbCheck, TbMinus, TbPlus } from "react-icons/tb";
import AppLogo from "components/ds/AppLogo";
import type { FlatRequestItem } from "../types";
import { catalogLinkTenant } from "state/members/access_v2/catalogNames";

const MobileCatalogCard = ({
  item,
  added,
  onToggle,
  hasAccess = false,
}: {
  item: FlatRequestItem;
  added: boolean;
  onToggle: () => void;
  /** The member already holds active access to this card's resource. */
  hasAccess?: boolean;
}) => (
  <Box
    as="button"
    type="button"
    onClick={onToggle}
    aria-pressed={added}
    position="relative"
    display="flex"
    flexDirection="column"
    w="full"
    minH="148px"
    p="14px"
    gap="2xs" // 4px
    textAlign="left"
    bg={added ? "azurite.50" : "neutral.0"}
    borderWidth="1px"
    borderStyle="solid"
    borderColor={added ? "azurite.200" : "transparent"}
    borderRadius="component.2xl" // 12px
    boxShadow="elevLow"
    cursor="pointer"
    transition="border-color 120ms cubic-bezier(0.23,1,0.32,1), box-shadow 120ms cubic-bezier(0.23,1,0.32,1), transform 120ms cubic-bezier(0.23,1,0.32,1), background 120ms cubic-bezier(0.23,1,0.32,1)"
    _hover={{ boxShadow: "elevHigh" }}
    _active={{ transform: "scale(0.97)" }}
    _focusVisible={{
      outline: "none",
      borderColor: "p.400",
      boxShadow: "focusRingSubtle",
    }}
  >
    {added && (
      <Box
        position="absolute"
        top="2"
        right="2"
        w="20px"
        h="20px"
        borderRadius="full"
        bg="success.500"
        color="white"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        aria-hidden
      >
        <TbCheck size={13} strokeWidth={3} />
      </Box>
    )}

    <Box mb="sm" display="flex">
      <AppLogo
        size="lg"
        name={item.catalogItem.name}
        logoUrl={item.catalogItem.imageUrl || undefined}
      />
    </Box>

    <Box
      as="span"
      fontSize="15px"
      fontWeight={700}
      color="neutral.950"
      lineHeight="1.2"
    >
      {item.resource.name}
    </Box>

    {catalogLinkTenant(item.resource) && (
      <Box
        as="span"
        fontSize="smd"
        fontWeight={500}
        color="neutral.800"
        lineHeight="1.3"
      >
        {catalogLinkTenant(item.resource)}
      </Box>
    )}

    <Box as="span" fontSize="xs" color="neutral.700" lineHeight="1.3">
      {item.catalogName.replace(/ catalog$/i, "")} · {item.catalogItem.name}
    </Box>

    {hasAccess && (
      <Box
        as="span"
        mt="2xs"
        alignSelf="flex-start"
        display="inline-flex"
        alignItems="center"
        gap="2xs"
        px="6px"
        py="1px"
        borderRadius="full"
        bg="success.50"
        color="success.700"
        fontSize="xs"
        fontWeight={600}
        lineHeight="1.3"
      >
        <Icon as={TbCheck} boxSize="11px" strokeWidth={3} />
        You have access
      </Box>
    )}

    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      gap="1xs" // 6px
      fontSize="smd" // 13px
      fontWeight={500}
      color={added ? "success.600" : "neutral.700"}
      mt="auto"
      pt="sm"
    >
      {added ? (
        <>
          <TbMinus size={15} />
          Added
        </>
      ) : (
        <>
          <TbPlus size={15} />
          Add
        </>
      )}
    </Box>
  </Box>
);

export default MobileCatalogCard;
